import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, Send, ArrowLeft, ShieldAlert, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SellerMessages() {
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchConversations(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/seller/messages`, { headers });
      setConversations(res.data.conversations || []);
    } catch (err) { toast.error('Mesajlar yuklenemedi'); }
    finally { setLoading(false); }
  };

  const openConversation = async (conv) => {
    setSelectedConv(conv);
    try {
      const res = await axios.get(`${API}/seller/messages/${conv.conversation_id}`, { headers });
      setMessages(res.data.messages || []);
    } catch (err) { toast.error('Mesajlar yuklenemedi'); }
  };

  const sendReply = async () => {
    if (!newMessage.trim() || !selectedConv) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/seller/messages/${selectedConv.conversation_id}/reply`,
        { message: newMessage.trim() }, { headers }
      );
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mesaj gonderilemedi');
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h1 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
        <MessageSquare className="h-6 w-6 text-orange-500" /> Musteri Mesajlari
      </h1>

      {/* Warning for sellers */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 flex items-start gap-2">
        <ShieldAlert className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm text-amber-800 font-medium">Iletisim Bilgisi Paylasimi Yasaktir</p>
          <p className="text-xs text-amber-700 mt-1">
            Mesajlarda telefon numarasi, adres, email veya sosyal medya bilgisi paylasmak yasaktir.
            Ihlal durumunda magazaniz askiya alinabilir.
          </p>
        </div>
      </div>

      <div className="flex gap-4 min-h-[400px]">
        {/* Conversation List */}
        <div className={`${selectedConv ? 'hidden md:block' : ''} w-full md:w-1/3 space-y-2`}>
          {loading ? (
            <div className="text-center py-10"><div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto" /></div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">Henuz mesaj yok</div>
          ) : conversations.map(c => (
            <button key={c.conversation_id} onClick={() => openConversation(c)}
              className={`w-full text-left p-3 rounded-xl border transition-all ${
                selectedConv?.conversation_id === c.conversation_id 
                  ? 'bg-orange-50 border-orange-300' : 'bg-white hover:bg-slate-50'
              }`}
              data-testid={`conv-${c.conversation_id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <User className="h-4 w-4 text-slate-400" />
                <span className="font-medium text-sm text-slate-900">{c.customer_name}</span>
              </div>
              <p className="text-xs text-slate-500 truncate">{c.last_message}</p>
              <p className="text-xs text-slate-400 mt-1">{new Date(c.last_date).toLocaleString('tr-TR')}</p>
            </button>
          ))}
        </div>

        {/* Chat Area */}
        <div className={`${selectedConv ? '' : 'hidden md:flex'} flex-1 bg-white rounded-xl border flex flex-col`}>
          {selectedConv ? (
            <>
              <div className="p-3 border-b flex items-center gap-2">
                <button onClick={() => setSelectedConv(null)} className="md:hidden p-1">
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <User className="h-5 w-5 text-slate-400" />
                <span className="font-medium text-sm">{selectedConv.customer_name}</span>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[400px]">
                {messages.map(msg => (
                  <div key={msg.id} className={`flex ${msg.sender_type === 'seller' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                      msg.sender_type === 'seller'
                        ? 'bg-orange-500 text-white rounded-br-none'
                        : 'bg-slate-100 text-slate-800 rounded-bl-none'
                    }`}>
                      <p>{msg.message}</p>
                      <p className={`text-xs mt-1 ${msg.sender_type === 'seller' ? 'text-orange-200' : 'text-slate-400'}`}>
                        {new Date(msg.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <div className="border-t p-3 flex gap-2">
                <input value={newMessage} onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendReply()}
                  placeholder="Cevabinizi yazin..." disabled={sending}
                  className="flex-1 border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
                  data-testid="seller-reply-input"
                />
                <button onClick={sendReply} disabled={sending || !newMessage.trim()}
                  className="bg-orange-500 text-white rounded-xl px-3 py-2 hover:bg-orange-600 disabled:opacity-50"
                  data-testid="seller-send-btn"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
              Bir konusma secin
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
